import type { RefObject, UIEvent } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flipped } from 'react-flip-toolkit';
import confetti from 'canvas-confetti';

import type { GetUserQuestionAnswersRankingResponse } from '@genially/contracts';
import { useResizeObserver } from '@genially/document-components';
import { calculate } from '@genially/utils';

import { useIsMounted } from '../../../../../../hooks';
import { isMobile } from '../../../../../../utils/devices/device';
import { UserAliasAvatar } from '../../../../../UserAliasAvatar/UserAliasAvatar';
import {
  PositionChange,
  useCalculateRankingPositionChanges,
} from '../../../../hooks/useCalculateRankingPositionChanges';
import { useSidebarSummaryWithContentContext } from '../../SidebarSummaryWithContent.context';
import { EmptyItem } from './components/EmptyItem/EmptyItem';
import { PodiumUser } from './components/PodiumUser/PodiumUser';
import { RankingItem } from './components/RankingItem/RankingItem';
import { useRankingSidebarData } from './hooks/useRankingSidebarData';
import type { MedalPosition } from './types/MedalPosition';
import { GridColumnGap, GridRowGap, MaxItemHeight, MaxItemWidth } from './constants';
import { StyledBigSubtitle } from '../../../../SidebarSummary.styled';
import {
  Content,
  ContentWrapper,
  EmptyItemWrapper,
  FullWidthTitleWrapper,
  HeaderAvatarsWrapper,
  HeaderBackground,
  HeaderWrapper,
  LeftNavigationButton,
  PaddingWrapper,
  ParticipantsWrapper,
  RankingItemWrapper,
  RightNavigationButton,
  ScrollWrapper,
  StyledBiggerSubtitle,
  StyledCanvas,
  StyledFlipper,
  StyledLeftArrow,
  StyledRightArrow,
  Wrapper,
} from './RankingSummary.styled';

interface RankingSummaryDataProps {
  isFullWidthEnabled?: boolean;
  isAfterLastAnimation?: boolean;
  handleLastAnimationEnd?: () => void;
}

const GRADIENT_THRESHOLD = 10;

const PODIUM_DATA = [
  {
    medalPosition: 'second' as MedalPosition,
    index: 2,
    avatarSize: UserAliasAvatar.Size.Big,
  },
  {
    medalPosition: 'first' as MedalPosition,
    index: 1,
    avatarSize: UserAliasAvatar.Size.Larger,
  },
  {
    medalPosition: 'third' as MedalPosition,
    index: 3,
    avatarSize: UserAliasAvatar.Size.Medium,
  },
];

const FULL_SIZE_PODIUM_DATA = [
  {
    medalPosition: 'first' as MedalPosition,
    index: 1,
    avatarSize: UserAliasAvatar.Size.Larger,
  },
  {
    medalPosition: 'second' as MedalPosition,
    index: 2,
    avatarSize: UserAliasAvatar.Size.Big,
  },
  {
    medalPosition: 'third' as MedalPosition,
    index: 3,
    avatarSize: UserAliasAvatar.Size.Medium,
  },
];

const DELAY_BETWEEN_LIST_ITEM_LAST_ANIMATION = 0.85;
const DELAY_BETWEEN_PODIUM_LAST_ANIMATION = 0.85;

export const RankingSummary = ({
  isFullWidthEnabled,
  isAfterLastAnimation,
  handleLastAnimationEnd,
}: RankingSummaryDataProps) => {
  const {
    currentUser: { alias: currentUserAlias, isAuthenticated },
    config: { getTranslation, disablePodiumAnimations },
  } = useSidebarSummaryWithContentContext();

  const currentUserRef = useRef<HTMLDivElement>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [autoScrollTimeout, setAutoScrollTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hasScrollTop, setHasScrollTop] = useState(false);
  const [hasScrollBottom, setHasScrollBottom] = useState(true);
  const [hasScrollLeft, setHasScrollLeft] = useState(false);
  const [hasScrollRight, setHasScrollRight] = useState(true);
  const [participantsGridWidth, setParticipantsGridWidth] = useState(0);
  const [emptyItems, setEmptyItems] = useState<null[]>([]);
  const [isPlayingLastAnimation, setIsPlayingLastAnimation] = useState(
    isAfterLastAnimation && !disablePodiumAnimations,
  );

  const [visibleItemsOnScroll, setVisibleItemsOnScroll] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement>();

  const getIsMounted = useIsMounted();

  const participantsWrapperRef = useRef<HTMLDivElement>(null);

  const { aggregatedData, totalData } = useRankingSidebarData();

  const { rankingChanges, resetCalculations } = useCalculateRankingPositionChanges({
    currentData: aggregatedData,
  });

  const handleChangePositionTimeout = useCallback(() => {
    resetCalculations();
  }, [resetCalculations]);

  const currentUserData = aggregatedData.find(
    user =>
      user.userAlias === currentUserAlias &&
      ((isAuthenticated && user.geniallyUserId) ||
        (!isAuthenticated && !user.geniallyUserId)),
  );

  const currentUser = useMemo(
    () => ({
      geniallyUserId: currentUserData?.geniallyUserId || '',
      userAlias: currentUserAlias ?? '',
      rightAnswers: currentUserData?.rightAnswers || 0,
      wrongAnswers: currentUserData?.wrongAnswers || 0,
    }),
    [
      currentUserData?.geniallyUserId,
      currentUserData?.rightAnswers,
      currentUserData?.wrongAnswers,
      currentUserAlias,
    ],
  );

  const firstParticipants: GetUserQuestionAnswersRankingResponse | undefined[] =
    aggregatedData
      .slice(0, 3)
      .concat(Array(Math.max(0, 3 - aggregatedData.length)).fill(undefined));

  const orderedFirstParticipants = isFullWidthEnabled
    ? firstParticipants
    : [firstParticipants[1], firstParticipants[0], firstParticipants[2]];
  const restParticipants = isMobile ? aggregatedData : aggregatedData.slice(3);

  const setGradients = (element: HTMLDivElement) => {
    setHasScrollTop(element.scrollTop >= GRADIENT_THRESHOLD);
    setHasScrollBottom(
      element.scrollHeight - element.scrollTop - element.clientHeight >=
        GRADIENT_THRESHOLD,
    );

    setHasScrollLeft(element.scrollLeft >= GRADIENT_THRESHOLD);
    setHasScrollRight(
      element.scrollWidth - element.scrollLeft - element.clientWidth >=
        GRADIENT_THRESHOLD,
    );
  };

  const setGridWidth = () => {
    if (isFullWidthEnabled && participantsWrapperRef.current) {
      const lastChild = (
        participantsWrapperRef.current.childNodes as NodeListOf<HTMLDivElement>
      )[participantsWrapperRef.current.children.length - 1];

      if (lastChild) {
        setParticipantsGridWidth(lastChild.offsetLeft + lastChild.offsetWidth);
      }
    }
  };

  const calculateHowManyVisibleRankingItems = (scrollElement?: HTMLDivElement | null) => {
    if (!scrollElement) {
      return 0;
    }

    const containerHeight = scrollElement.offsetHeight;
    const fillRow = Math.floor(
      (containerHeight + GridRowGap) / (MaxItemHeight + GridRowGap),
    );

    let items = fillRow;

    if (isFullWidthEnabled) {
      const containerWidth = scrollElement.offsetWidth - GridColumnGap;

      const columns = Math.floor(
        (containerWidth + GridColumnGap) / (MaxItemWidth + GridColumnGap),
      );

      items = columns * fillRow;
    }

    return items;
  };

  const calculateEmptyItems = () => {
    const max = Math.max(visibleItemsOnScroll - restParticipants.length, 0);

    if (max === emptyItems.length) {
      return;
    }

    setEmptyItems(Array(max).fill(null));
  };

  const scrollRef = useResizeObserver(() => {
    if (scrollRef.current) {
      setGradients(scrollRef.current);
      setGridWidth();
      setVisibleItemsOnScroll(calculateHowManyVisibleRankingItems(scrollRef.current));
      calculateEmptyItems();
    }
  });

  const handleAutoScroll = useCallback(() => {
    setAutoScrollTimeout(prev => {
      if (prev) {
        clearTimeout(prev);
      }

      return setTimeout(() => {
        setIsAutoScrolling(false);

        if (scrollRef.current) {
          scrollRef.current.removeEventListener('scroll', handleAutoScroll);
        }
      }, 100);
    });
  }, [scrollRef]);

  useEffect(() => {
    if (
      scrollRef.current &&
      currentUserRef.current &&
      isAutoScrollEnabled &&
      !isAutoScrolling &&
      !isPlayingLastAnimation
    ) {
      const currentUserBoundingRect = currentUserRef.current.getBoundingClientRect();
      const scrollBoundingRect = scrollRef.current.getBoundingClientRect();

      if (
        (!isFullWidthEnabled &&
          !(
            Math.floor(currentUserBoundingRect.top - scrollBoundingRect.top) >= 0 &&
            Math.floor(currentUserBoundingRect.bottom - scrollBoundingRect.bottom) <= 0
          )) ||
        (isFullWidthEnabled &&
          !(
            Math.floor(currentUserBoundingRect.left - scrollBoundingRect.left) >= 0 &&
            Math.floor(currentUserBoundingRect.right - scrollBoundingRect.right) <= 0
          ))
      ) {
        setIsAutoScrolling(true);

        scrollRef.current.removeEventListener('scroll', handleAutoScroll);
        scrollRef.current.addEventListener(
          'scroll',

          handleAutoScroll,
          { passive: true },
        );

        currentUserRef.current.scrollIntoView(
          isFullWidthEnabled
            ? { behavior: 'smooth', inline: 'center' }
            : { behavior: 'smooth', block: 'center' },
        );

        // call one time to ensure that autoscrolling is disabled if scrollIntoView is not working
        handleAutoScroll();
      }
    }
  }, [
    currentUserAlias,
    currentUser.geniallyUserId,
    isAutoScrollEnabled,
    aggregatedData,
    handleAutoScroll,
    scrollRef,
    getIsMounted,
    isAutoScrolling,
    isFullWidthEnabled,
    isPlayingLastAnimation,
  ]);

  useEffect(() => {
    return () => {
      if (!getIsMounted() && autoScrollTimeout) {
        clearTimeout(autoScrollTimeout);
      }
    };
  }, [autoScrollTimeout, getIsMounted]);

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (isAutoScrollEnabled && !isAutoScrolling) {
        setIsAutoScrollEnabled(false);
      }

      const element = e.currentTarget;

      setGradients(element);
    },
    [isAutoScrollEnabled, isAutoScrolling],
  );

  useEffect(() => {
    if (scrollRef.current) {
      setGradients(scrollRef.current);
    }
  }, [scrollRef]);

  const handleScrollToLeft = () => {
    if (
      !isFullWidthEnabled ||
      !participantsWrapperRef.current ||
      !scrollRef.current ||
      scrollRef.current.scrollLeft === 0
    ) {
      return;
    }

    if (isAutoScrollEnabled && !isAutoScrolling) {
      setIsAutoScrollEnabled(false);
    }

    const element = Array.from(
      participantsWrapperRef.current.childNodes as NodeListOf<HTMLDivElement>,
    )
      .reverse()
      .find(child => {
        const { left } = child.getBoundingClientRect();

        if (!scrollRef.current) return false;

        return left - scrollRef.current.getBoundingClientRect().left < 0;
      });

    if (element) {
      scrollRef.current.scrollTo({ left: element.offsetLeft - 32, behavior: 'smooth' });
    }
  };

  const handleScrollToRight = () => {
    if (
      !isFullWidthEnabled ||
      !participantsWrapperRef.current ||
      !scrollRef.current ||
      scrollRef.current.scrollLeft + scrollRef.current.clientWidth ===
        scrollRef.current?.scrollWidth
    ) {
      return;
    }

    if (isAutoScrollEnabled && !isAutoScrolling) {
      setIsAutoScrollEnabled(false);
    }

    const scrollPositionX = scrollRef.current.scrollLeft + scrollRef.current.offsetWidth;

    const element = Array.from(
      participantsWrapperRef.current.childNodes as NodeListOf<HTMLDivElement>,
    ).find(child => {
      return child.offsetLeft + child.offsetWidth > scrollPositionX;
    });

    if (element) {
      scrollRef.current.scrollTo({
        left:
          element.offsetLeft + element.offsetWidth + 64 - scrollRef.current.offsetWidth,
        behavior: 'smooth',
      });
    }
  };

  const getContentChildren = () => {
    const startIndex = isMobile ? 1 : 4;

    return (restParticipants as GetUserQuestionAnswersRankingResponse).map(
      (question, index, arr) => {
        const isCurrentUser =
          question.userAlias === currentUserAlias &&
          question.geniallyUserId === currentUser.geniallyUserId;

        const medalPosition: MedalPosition | undefined =
          isMobile && index < 3
            ? (['first', 'second', 'third'] as MedalPosition[])[index]
            : undefined;

        const participantPositionChange = calculate(() => {
          const positionChange = rankingChanges.get(question.userAlias);

          return positionChange || PositionChange.SAME;
        });

        const enterDelayAnimation =
          index > visibleItemsOnScroll
            ? 0
            : (Math.min(arr.length, visibleItemsOnScroll) - index) *
              DELAY_BETWEEN_LIST_ITEM_LAST_ANIMATION;

        return (
          <RankingItemWrapper key={`${question.userAlias}-${question.geniallyUserId}`}>
            {isPlayingLastAnimation && (
              <EmptyItemWrapper>
                <EmptyItem key={`empty-item-${question.userAlias}`} />
              </EmptyItemWrapper>
            )}

            <Flipped
              flipId={`${question.userAlias}-${question.geniallyUserId}`}
              stagger={false}
              spring="noWobble"
            >
              <RankingItem
                ref={isCurrentUser ? currentUserRef : null}
                question={question}
                positionChange={participantPositionChange}
                isCurrentUser={isCurrentUser}
                medalPosition={medalPosition}
                index={index + startIndex}
                zIndex={arr.length - index}
                onChangePositionTimeout={handleChangePositionTimeout}
                enterAnimation={isPlayingLastAnimation}
                enterDelayAnimation={enterDelayAnimation}
              />
            </Flipped>
          </RankingItemWrapper>
        );
      },
    );
  };

  const getEmptyItems = () => {
    // eslint-disable-next-line react/no-array-index-key
    return emptyItems.map((_, index) => <EmptyItem key={`empty-item-${index}`} />);
  };

  const runConfetti = async () => {
    const confettiElement = confetti.create(canvasRef.current, {
      resize: true,
    });

    const defaults = {
      spread: 70,
      startVelocity: 30,
      zIndex: 1000,
    };

    const conffetiLeftDefaults = {
      ...defaults,
      angle: 60,
      origin: { x: 0, y: 1 },
    };

    const conffetiRightDefaults = {
      ...defaults,
      angle: 120,
      origin: { x: 1, y: 1 },
    };

    confettiElement({
      ...conffetiLeftDefaults,
      disableForReducedMotion: true,
    });

    await confettiElement({
      ...conffetiRightDefaults,
      disableForReducedMotion: true,
    });

    setIsAutoScrollEnabled(true);
    setIsPlayingLastAnimation(false);
    handleLastAnimationEnd?.();
  };

  const handleAnimationEnd = () => {
    runConfetti();
  };

  return (
    <Wrapper $isFullWidthEnabled={isFullWidthEnabled}>
      {isFullWidthEnabled && (
        <FullWidthTitleWrapper>
          <StyledBiggerSubtitle>
            {totalData}{' '}
            {getTranslation(
              '__new.content.interactiveQuestion.sidebar.ranking.participants',
            )}
          </StyledBiggerSubtitle>
        </FullWidthTitleWrapper>
      )}
      <Content $isFullWidthEnabled={isFullWidthEnabled}>
        {!(isMobile && isFullWidthEnabled) && (
          <HeaderWrapper $isFullWidthEnabled={isFullWidthEnabled}>
            {!isFullWidthEnabled && (
              <Fragment>
                {!isMobile && <HeaderBackground />}

                <StyledBigSubtitle>
                  {totalData}{' '}
                  {getTranslation(
                    '__new.content.interactiveQuestion.sidebar.ranking.participants',
                  )}
                </StyledBigSubtitle>
              </Fragment>
            )}

            {!isMobile && (
              <HeaderAvatarsWrapper $isFullWidthEnabled={isFullWidthEnabled}>
                {orderedFirstParticipants.map((participant, index) => {
                  const getPodiumData = () => {
                    if (isFullWidthEnabled) {
                      return FULL_SIZE_PODIUM_DATA[index];
                    }

                    return PODIUM_DATA[index];
                  };

                  const participantPositionChange = calculate(() => {
                    if (!participant) {
                      return PositionChange.SAME;
                    }

                    const positionChange = rankingChanges.get(participant.userAlias);

                    return positionChange || PositionChange.SAME;
                  });

                  const isCurrentUser =
                    participant &&
                    participant.userAlias === currentUserAlias &&
                    participant.geniallyUserId === currentUser.geniallyUserId;

                  const podiumData = getPodiumData();

                  const enterDelayAnimation =
                    Math.min(restParticipants.length, visibleItemsOnScroll) *
                      DELAY_BETWEEN_LIST_ITEM_LAST_ANIMATION +
                    (orderedFirstParticipants.length - podiumData.index) *
                      DELAY_BETWEEN_PODIUM_LAST_ANIMATION;

                  return (
                    <PodiumUser
                      key={podiumData.medalPosition}
                      medalPosition={podiumData.medalPosition}
                      podiumIndex={podiumData.index}
                      podiumSize={podiumData.avatarSize}
                      isActive={isCurrentUser}
                      userAlias={participant?.userAlias}
                      hasEnterAnimation={isPlayingLastAnimation}
                      enterDelayAnimation={enterDelayAnimation}
                      positionChange={participantPositionChange}
                      onChangePositionTimeout={handleChangePositionTimeout}
                      onEnterAnimationEnd={
                        isPlayingLastAnimation && podiumData.index === 1
                          ? handleAnimationEnd
                          : undefined
                      }
                    />
                  );
                })}
              </HeaderAvatarsWrapper>
            )}

            {isPlayingLastAnimation && (
              <StyledCanvas ref={canvasRef as RefObject<HTMLCanvasElement>} />
            )}
          </HeaderWrapper>
        )}

        <ScrollWrapper
          $isVerticalScroll={!isFullWidthEnabled}
          $hasScrollStart={isFullWidthEnabled ? hasScrollLeft : hasScrollTop}
          $hasScrollEnd={isFullWidthEnabled ? hasScrollRight : hasScrollBottom}
        >
          {isFullWidthEnabled && (
            <Fragment>
              <LeftNavigationButton onClick={handleScrollToLeft}>
                <StyledLeftArrow />
              </LeftNavigationButton>
              <RightNavigationButton onClick={handleScrollToRight}>
                <StyledRightArrow />
              </RightNavigationButton>
            </Fragment>
          )}
          <PaddingWrapper $isFullWidthEnabled={isFullWidthEnabled}>
            <ContentWrapper ref={scrollRef} onScroll={handleScroll}>
              <StyledFlipper
                flipKey={restParticipants
                  .map(participant => participant.userAlias)
                  .join('')}
                spring="noWobble"
              >
                <ParticipantsWrapper
                  $width={participantsGridWidth}
                  $isFullWidthEnabled={isFullWidthEnabled}
                  ref={participantsWrapperRef}
                >
                  {getContentChildren()}
                  {getEmptyItems()}
                </ParticipantsWrapper>
              </StyledFlipper>
            </ContentWrapper>
          </PaddingWrapper>
        </ScrollWrapper>
      </Content>
    </Wrapper>
  );
};
